import gsap from 'gsap';
import { Container, Sprite, Texture } from 'pixi.js';
import { SignalConnection } from 'typed-signals';

import { App, Shared } from '../../App';
import { RectangleButton } from '../buttons/RectangleButton';
import LocalisedText from '../LocalisedText';
import { Panel } from '../Panel';

const defaultOpts = {
    title: '',
    message: '',
    confirm: '',
    cancel: '',
    onConfirm: undefined as (() => void) | undefined,
};

export type ConfirmPopUpOptions = typeof defaultOpts;

export class ConfirmPopUp
{
    public app: App;
    public view: Container;
    public content: Container;
    public panel: Panel;
    public shade: Sprite;
    public title: LocalisedText;
    public message: LocalisedText;
    public confirmButton: RectangleButton;
    public cancelButton: RectangleButton;
    public presenting = false;
    public options: ConfirmPopUpOptions;

    private resizeConnection?: SignalConnection;

    constructor(app: App, opts: Partial<ConfirmPopUpOptions> = {})
    {
        this.app = app;
        this.view = new Container();
        this.options = { ...defaultOpts, ...opts };

        this.shade = new Sprite(Texture.WHITE);
        this.shade.tint = 0x000000;
        this.shade.interactive = true;
        this.shade.anchor.set(0.5);
        this.shade.alpha = 0.9;
        this.view.addChild(this.shade);

        this.content = new Container();
        this.view.addChild(this.content);

        this.panel = new Panel({ w: 600, h: 350 });
        this.content.addChild(this.panel.view);

        this.title = new LocalisedText('resetPopup_title', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 60,
            maxWidth: 500,
        });
        this.title.anchor.set(0.5);
        this.title.y = (-this.panel.bg.height / 2) + 50;
        this.content.addChild(this.title);

        this.message = new LocalisedText('resetPopup_message', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 36,
            lineHeight: 36,
            maxWidth: 500,
            wordWrap: true,
            wordWrapWidth: 500,
            maxHeight: 200
        });
        this.message.anchor.set(0.5);
        this.message.y = 0;
        this.content.addChild(this.message);

        this.confirmButton = new RectangleButton({
            text: 'resetPopup_confirm',
            fontSize: 50,
            bg: 'magenta',
            w: 250,
            h: 100,
            startHidden: true,
        });
        this.view.addChild(this.confirmButton.view);
        this.confirmButton.onPress.connect(() =>
        {
            if (this.options.onConfirm) this.options.onConfirm();
            this.dismiss();
        });

        this.cancelButton = new RectangleButton({
            text: 'resetPopup_cancel',
            fontSize: 50,
            bg: 'yellow',
            w: 250,
            h: 100,
            startHidden: true,
        });
        this.view.addChild(this.cancelButton.view);
        this.cancelButton.onPress.connect(() =>
        {
            this.dismiss();
        });

        this.resize();
    }

    public async present(opts: Partial<ConfirmPopUpOptions> = {}): Promise<void>
    {
        if (this.presenting) return;
        this.presenting = true;
        Object.assign(this.options, opts);

        this.title.setText(this.options.title);
        this.message.setText(this.options.message);
        this.confirmButton.text = this.options.confirm;
        this.cancelButton.text = this.options.cancel;

        this.app.stage.stage.addChild(this.view);
        this.resizeConnection = this.app.resize.onResize.connect(() => this.resize());
        this.resize();

        this.killTweens();

        this.content.scale.set(0.5);
        this.content.alpha = 1;
        this.confirmButton.view.scale.set(0);
        this.cancelButton.view.scale.set(0);
        this.shade.alpha = 0.00001;

        Shared.game.soundSystem.playSfx('windmill');

        gsap.to(this.shade, { alpha: 0.75, duration: 0.2 });
        await gsap.to(this.content.scale, { x: 1, y: 1, duration: 0.3, ease: 'back.out' });
        await this.confirmButton.show();
        await this.cancelButton.show();
    }

    public async dismiss(): Promise<void>
    {
        if (!this.presenting) return;

        this.presenting = false;
        this.killTweens();
        if (this.resizeConnection)
        {
            this.resizeConnection.disconnect();
            this.resizeConnection = undefined;
        }

        this.confirmButton.hide();
        this.cancelButton.hide();
        gsap.to(this.content.scale, { x: 1.5, y: 1.5, duration: 0.3, ease: 'back.in' });
        gsap.to(this.content, { alpha: 0, duration: 0.3, ease: 'sine.in' });
        await gsap.to(this.shade, { alpha: 0, duration: 0.2, delay: 0.1 });

        if (this.view.parent) this.view.parent.removeChild(this.view);
    }

    public resize(): void
    {
        this.view.x = this.app.resize.width / 2;
        this.view.y = this.app.resize.height / 2;
        this.shade.width = this.app.resize.width;
        this.shade.height = this.app.resize.height;

        this.confirmButton.view.x = -140;
        this.confirmButton.view.y = this.panel.bg.height / 2;
        this.cancelButton.view.x = -this.confirmButton.view.x;
        this.cancelButton.view.y = this.confirmButton.view.y;
    }

    private killTweens(): void
    {
        gsap.killTweensOf(this.shade);
        gsap.killTweensOf(this.confirmButton.view);
        gsap.killTweensOf(this.cancelButton.view);
        gsap.killTweensOf(this.content);
        gsap.killTweensOf(this.content.scale);
    }
}
